import { useState, useEffect } from "react";
import { Eye, Shield, X } from "lucide-react";
import {
  clearImpersonationStorage,
  supabase,
} from "../lib/supabase";

/**
 * Floating banner shown when admin is impersonating a user.
 * Reads from sessionStorage('admin_impersonation') set by the impersonate page.
 * Provides a button to end the session (sign out + close tab).
 */
export default function ImpersonationBanner() {
  const [info, setInfo] = useState(null);
  const [ending, setEnding] = useState(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("admin_impersonation");
      if (raw) setInfo(JSON.parse(raw));
    } catch {}
  }, []);

  if (!info) return null;

  const handleEnd = async () => {
    if (ending) return;
    setEnding(true);
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch {
      // best-effort — continue to navigate even if signOut fails
    } finally {
      supabase.auth.stopAutoRefresh();
      sessionStorage.removeItem("admin_impersonation");
      clearImpersonationStorage();
    }
    // Try to close the tab (works only if the tab was opened via JS).
    // If that fails, reload with normal localStorage auth so the preserved
    // administrator session is restored in this tab.
    window.close();
    setTimeout(() => {
      window.location.assign("/admin/dashboard");
    }, 150);
  };

  return (
    <div className="fixed bottom-2 left-3 right-3 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:max-w-[calc(100vw-2rem)] z-[60] bg-amber-500 text-white px-3 py-2 sm:px-4 rounded-xl shadow-lg flex items-center justify-center gap-2 text-xs sm:text-sm font-medium">
      <Eye size={15} className="shrink-0" />
      <span className="min-w-0 truncate" role="status">
        Viewing as <strong>{info.targetName || info.targetEmail}</strong>
      </span>
      <button
        type="button"
        onClick={handleEnd}
        disabled={ending}
        aria-label="End impersonation session"
        title="End impersonation session"
        className="shrink-0 inline-flex items-center gap-1 p-2 bg-white/20 hover:bg-white/30 rounded-lg text-[11px] sm:text-xs font-medium transition-colors disabled:opacity-60"
      >
        <X size={12} />
      </button>
    </div>
  );
}
